import React from 'react'
import creditScoreBanner from '@/assets/application-flow-skins/shared/result/result-credit-score-banner.png'
import ApplicationFlowHeader from '../components/application-flow-header'
import ApplicationFlowFeaturedProductCarousel from '../components/application-flow-featured-product-carousel'
import { ResultSkinProps } from '../types'
import themeStyles from '../theme/application-flow-theme.module.scss'
import styles from './result-skin.module.scss'

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US')
const STATIC_ANNUAL_INTEREST_RATE = '7.2%～23.98%'
// 信用分 Banner 等待后端下发数据后再开放，当前仅保留结构和样式，不参与展示。
const SHOW_CREDIT_SCORE_BANNER = false

const formatAmount = (value: ResultSkinProps['model']['applyLimit']): string => {
  const normalized = String(value || '')
  const amountMap: Record<string, string> = {
    1: '30,000',
    2: '50,000',
    3: '100,000',
    4: '200,000',
  }

  if (amountMap[normalized]) {
    return amountMap[normalized]
  }

  const numericValue = Number(normalized)
  return Number.isFinite(numericValue) && normalized ? NUMBER_FORMATTER.format(numericValue) : normalized || '50,000'
}

const getResultCopy = (
  status: ResultSkinProps['model']['status'],
): { description: string; title: string } => {
  if (status === 'match-failed') {
    return {
      description: '您的资质暂不符合要求，请30天后再试',
      title: '很遗憾，暂无匹配产品',
    }
  }
  if (status === 'application-failed') {
    return {
      description: '点击下方按钮可重新申请额度',
      title: '糟糕，额度已被抢完',
    }
  }
  return {
    description: '领取后客户经理将在30分钟内来电审核',
    title: '领取成功，等待致电',
  }
}

/**
 * ResultSkin — 申请结果的纯展示皮肤，所有结果和下载决策均由旧容器预先给出。
 * @returns {React.ReactElement} 结果展示节点
 */
const ResultSkin: React.FC<ResultSkinProps> = React.memo(({
  model,
  onApplyFeaturedProduct,
  onBack,
  onDownload,
  onExposeFeaturedProduct,
  onApplyRecommendedProduct,
  onRetryApplication,
  theme,
}) => {
  const isSuccess = model.status === 'success'
  const isMatchFailed = model.status === 'match-failed'
  const isApplicationFailed = model.status === 'application-failed'

  const defaultCopy = getResultCopy(model.status)
  const title = model.title || defaultCopy.title
  const description = model.description || defaultCopy.description

  // 根据状态选取对应插图
  let illustration = theme.resultMatchFailedIllustration
  if (isSuccess) {
    illustration = theme.resultSuccessIllustration
  } else if (isApplicationFailed) {
    illustration = theme.resultApplicationFailedIllustration
  }

  return (
    <main className={themeStyles.themeRoot} data-theme={theme.name}>
      <section className={`${styles.page} ${styles[model.status]}`}>
        {/* 使用已有的通用 Header 组件，返回意图交由旧容器决定去向 */}
        <ApplicationFlowHeader onBack={onBack ?? (() => undefined)} title="匹配结果" visualType="solid" />
        <section className={styles.hero}>
          <img
            alt=""
            aria-hidden="true"
            className={styles.heroBackground}
            src={isMatchFailed ? theme.resultMatchFailedHeader : theme.resultHeader}
          />
          <div className={styles.heroInner}>
            {!isMatchFailed && model.showOfficialStatement !== false ? (
              <div className={styles.officialStatement}>
                <div className={styles.officialStatementHeader}>
                  <img alt="" aria-hidden="true" src={theme.resultOfficialStatementIcon} />
                  <strong>官方声明</strong>
                </div>
                <p>本平台所有服务免费，以官方名义收费，或在下款前以任何理由收费的行为，均为诈骗！切记！</p>
              </div>
            ) : null}
            <div className={styles.heroCopy}>
              <h1>{title}</h1>
              <p>{description}</p>
              {isSuccess ? (
                <div className={styles.applicationSummary}>
                  <span>需求金额：{formatAmount(model.applyLimit)}</span>
                  {model.productName ? <span>申请产品：{model.productName}</span> : null}
                </div>
              ) : null}
              {isApplicationFailed && onRetryApplication ? (
                <button className={styles.heroRetryButton} onClick={onRetryApplication} type="button">
                  重新提交额度申请
                </button>
              ) : null}
            </div>
          </div>
          <img alt="" aria-hidden="true" className={styles.heroIllustration} src={illustration} />
        </section>
        <main className={styles.content}>
          <ApplicationFlowFeaturedProductCarousel
            onApply={onApplyFeaturedProduct}
            onExpose={onExposeFeaturedProduct}
            products={model.featuredProducts || []}
          />
          {model.showDownloadApp && SHOW_CREDIT_SCORE_BANNER ? (
            <img
              alt=""
              aria-hidden="true"
              className={styles.creditScoreBanner}
              src={creditScoreBanner}
            />
          ) : null}
          {model.recommendedProducts?.length ? (
            <section className={styles.products}>
              <h2>为您推荐更多放款机构</h2>
              {model.recommendedProducts.map(product => (
                <article className={styles.productCard} key={product.id}>
                  <div className={styles.productMain}>
                    <div className={styles.productHeader}>
                      {product.logo ? (
                        <img alt="" aria-hidden="true" height={20} loading="lazy" src={product.logo} width={20} />
                      ) : null}
                      <strong>{product.name}</strong>
                      <em>初审通过</em>
                    </div>
                    <div className={styles.productBody}>
                      <div className={styles.productMetaItem}>
                        <span>最高可借</span>
                        <b>{formatAmount(product.limit)}</b>
                      </div>
                      <div className={styles.productMetaItem}>
                        <span>年化利率(单利)</span>
                        <b>{STATIC_ANNUAL_INTEREST_RATE}</b>
                      </div>
                    </div>
                  </div>
                  <button
                    className={styles.applyButton}
                    onClick={() => onApplyRecommendedProduct?.(product.id)}
                    type="button"
                  >
                    去申请
                  </button>
                </article>
              ))}
            </section>
          ) : null}
          {model.showDownloadApp ? (
            <button
              className={styles.downloadButton}
              disabled={model.downloadDisabled}
              onClick={onDownload}
              type="button"
            >
              {model.downloadLabel}
            </button>
          ) : null}
        </main>
      </section>
    </main>
  )
})

ResultSkin.displayName = 'ResultSkin'

export default ResultSkin
